import { useContext, useEffect, useMemo } from "react";
import { defineLabels, firstError, resolveLabels, unwrap, UpdatingPill, Widget, WidgetContentReadyContext, WidgetState, WidgetSkeleton, WidgetIssueState } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { WEIGHTED_TERMS_CONTRACT } from "./types";
import type { WeightedTerm, WeightedTermsData } from "./types";

export interface WeightedTermCloudConfig {
  title?: string;
  /** Per-instance overrides of the texts listed in `manifest.labels`. */
  labels?: Record<string, string>;
  /** Shows at most this many terms, heaviest first. */
  maxTerms?: number;
}

export const LABELS = defineLabels([
  {
    key: "weightName",
    name: "Weight name",
    default: "Observed streams",
    hint: "What the number next to each term measures. Shown in the tooltip and read by screen readers.",
  },
  { key: "emptyTitle", name: "Empty title", default: "No terms" },
  { key: "emptyDescription", name: "Empty description", default: "Nothing was reported for this period." },
] as const);

export type TermLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;

export const manifest: WidgetManifest = {
  id: "weighted-term-cloud",
  name: "Weighted Term Cloud",
  description:
    "Terms sized by weight, with the weight written next to each term; a ranked list with bars when narrow.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    terms: {
      source: "ott-piracy-terms",
      contract: WEIGHTED_TERMS_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: WeightedTermCloudConfig = {
  title: "Top terms",
  maxTerms: 20,
};

const DEFAULT_MAX_TERMS = 20;
const MIN_FONT_REM = 0.875;
const MAX_FONT_REM = 2.25;

const compactFormat = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const countFormat = new Intl.NumberFormat("en");

export function formatWeight(weight: number): string {
  return compactFormat.format(weight);
}

/** Heaviest first; ties keep a stable alphabetical order. Non-positive weights are dropped. */
export function rankTerms(data: WeightedTermsData, maxTerms = DEFAULT_MAX_TERMS): WeightedTerm[] {
  return data.terms
    .filter((term) => Number.isFinite(term.weight) && term.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.label.localeCompare(b.label))
    .slice(0, Math.max(1, maxTerms));
}

/**
 * Font size of a term. Linear on the square root of the weight, between the
 * smallest and the heaviest term of the list: an area-like scale that keeps a
 * dominant term from swallowing the rest. Deterministic, so the cloud never
 * moves between two refreshes with the same data.
 */
export function termFontSize(weight: number, min: number, max: number): number {
  if (max <= min) return (MIN_FONT_REM + MAX_FONT_REM) / 2;
  const ratio = (Math.sqrt(weight) - Math.sqrt(min)) / (Math.sqrt(max) - Math.sqrt(min));
  return Number((MIN_FONT_REM + Math.min(1, Math.max(0, ratio)) * (MAX_FONT_REM - MIN_FONT_REM)).toFixed(3));
}

export function describeTerm(term: WeightedTerm, labels: TermLabels): string {
  return `${term.label}: ${countFormat.format(term.weight)} ${labels.weightName.toLowerCase()}`;
}

function TermCloud({ terms, labels }: { terms: WeightedTerm[]; labels: TermLabels }) {
  const min = terms[terms.length - 1].weight;
  const max = terms[0].weight;
  return (
    <ol
      aria-label={labels.weightName}
      className="hidden flex-wrap items-baseline justify-center gap-x-6 gap-y-3 px-4 py-4 @xl:flex"
    >
      {terms.map((term, index) => (
        <li
          key={term.id}
          title={describeTerm(term, labels)}
          className="flex items-baseline gap-1.5 leading-tight"
        >
          <span
            className={`font-semibold ${index < 3 ? "text-primary" : "text-secondary"}`}
            style={{ fontSize: `${termFontSize(term.weight, min, max)}rem` }}
          >
            {term.label}
          </span>
          {/* Size is never the only carrier of the weight: the number is always written. */}
          <span className="text-xs tabular-nums text-muted">{formatWeight(term.weight)}</span>
        </li>
      ))}
    </ol>
  );
}

function TermRanking({ terms, labels }: { terms: WeightedTerm[]; labels: TermLabels }) {
  const max = terms[0].weight;
  return (
    <ol aria-label={labels.weightName} className="flex flex-col gap-2 px-4 py-3 @xl:hidden">
      {terms.map((term, index) => (
        <li key={term.id} title={describeTerm(term, labels)} className="flex items-center gap-3">
          <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted">{index + 1}</span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate font-medium text-primary">{term.label}</span>
              <span className="shrink-0 tabular-nums text-muted">{formatWeight(term.weight)}</span>
            </span>
            <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-surface-elevated" aria-hidden>
              <span
                className="block h-full rounded-full bg-brand-cyan"
                style={{ width: `${Math.max(2, (term.weight / max) * 100)}%` }}
              />
            </span>
          </span>
        </li>
      ))}
    </ol>
  );
}

function WeightedTermCloudComponent({ data, config }: WidgetProps<WeightedTermCloudConfig>) {
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const error = firstError(data, ["terms"]);
  const source = unwrap<WeightedTermsData>(data.terms);
  const isLoading = data.terms?.isLoading ?? false;
  const title = config.title?.trim() || defaultConfig.title || manifest.name;
  const labelsJson = JSON.stringify(resolveLabels(LABELS, config));
  const labels = useMemo(() => JSON.parse(labelsJson) as TermLabels, [labelsJson]);
  const terms = useMemo(() => (source ? rankTerms(source, config.maxTerms) : []), [source, config.maxTerms]);

  const isEmpty = !isLoading && !error && terms.length === 0;
  const firstLoad = isLoading && !source;
  const ready = !error && !isEmpty && !firstLoad && terms.length > 0;

  useEffect(() => {
    if (ready) markContentReady();
  }, [ready, markContentReady]);

  return (
    <Widget title={title} intrinsicHeight actions={isLoading && !firstLoad ? <UpdatingPill /> : undefined}>
      <div className="@container relative min-h-[14rem] w-full flex-1">
        {ready && (
          <>
            <TermCloud terms={terms} labels={labels} />
            <TermRanking terms={terms} labels={labels} />
          </>
        )}
        {error && (
          <WidgetIssueState error={error} className="absolute inset-0" />
        )}
        {isEmpty && (
          <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} className="absolute inset-0" />
        )}
        {firstLoad && !error && <WidgetSkeleton shape="chart" className="absolute inset-0" />}
      </div>
    </Widget>
  );
}

export const Component = WeightedTermCloudComponent;

const widgetModule: WidgetModule<WeightedTermCloudConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
